import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Calendar, ChevronUp, ChevronDown, Download, Lock } from 'lucide-react';
import useSWR from 'swr';
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../../../TranslationContext';

// Types
interface User {
  id: string;
  status: string; // 'Active', 'Locked', 'New'
  lockedDate?: string; // ISO date string when user was locked
  data: [];
}

interface PeriodOption {
  label: string;
  value: number; // in days
}

// Fetcher for SWR
const axiosFetcher = async (url: string) => {
  const response = await axios.get(url, {
    withCredentials:true,
    // headers: {
    //   Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    // },
  });
  return response.data;
};

export default function LockedUsersChart() {
  const { t } = useTranslation();
  const companyId = localStorage.getItem('company');
  const { appId } = useParams<{ appId: string }>();
  const API_BASE_URL = import.meta.env.VITE_API;
  
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>({ label: t('7_days'), value: 7 });
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  
  // Period options
  const PERIOD_OPTIONS: PeriodOption[] = [
    { label: t('Hours24'), value: 1 },
    { label: t('days7'), value: 7 },
    { label: t('days30'), value: 30 },
    { label: t('all_time'), value: 3650 },
  ];
  
  // Fetch user data
  const { data: usersData} = useSWR<User>(
    companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}/users` : null,
    axiosFetcher
  );

  // Calculate locked user statistics
  const [lockedUserStats, setLockedUserStats] = useState({
    percentage: 0,
    count: 0,
    totalUsers: 0,
    recentLocks: 0,
  });
  
  useEffect(() => {
    if (usersData?.data) {
      const now = new Date();
      const periodStart = new Date();
      periodStart.setDate(now.getDate() - selectedPeriod.value);
      
      const users = usersData.data;
      const totalUsers = users.length;
      
      if (totalUsers === 0) {
        setLockedUserStats({
          percentage: 0,
          count: 0,
          totalUsers: 0,
          recentLocks: 0,
        });
        return;
      }
      
      const lockedUsers = users.filter((user: { status: string; }) => user.status === 'Locked');
      const recentLocks = lockedUsers.filter((user: { lockedDate: string | number | Date; }) => {
        if (!user.lockedDate) return false;
        const lockDate = new Date(user.lockedDate);
        return lockDate >= periodStart;
      }).length;
      
      const percentage = Math.round((lockedUsers.length / totalUsers) * 100);
      setLockedUserStats({
        percentage,
        count: lockedUsers.length,
        totalUsers,
        recentLocks,
      });
    }
  }, [usersData, selectedPeriod]);

  // Chart data
  const chartData = [
    {
      name: t('locked_users'),
      value: lockedUserStats.percentage,
      fill: '#c06a70',
    },
    {
      name: t('active_users'),
      value: 100 - lockedUserStats.percentage,
      fill: '#879bae',
    },
  ];

  // // Download function
  // const handleDownload = async (format: 'png' | 'jpeg' | 'pdf') => {
  //   if (!chartRef.current) return;

  //   try {
  //     const chartElement = chartRef.current;
      
  //     const originalBackground = chartElement.style.backgroundColor;
  //     chartElement.style.backgroundColor = 'white';
  //     chartElement.style.padding = '20px';
      
  //     let dataUrl;
  //     if (format === 'pdf') {
  //       dataUrl = await htmlToImage.toPng(chartElement);
  //       const { jsPDF } = await import('jspdf');
  //       const pdf = new jsPDF('landscape');
  //       pdf.addImage(dataUrl, 'PNG', 15, 15, 260, 150);
  //       pdf.save(`${t('locked_users_kpi')}-${selectedPeriod.label}.pdf`);
  //     } else {
  //       dataUrl = await htmlToImage[`to${format.toUpperCase()}` as 'toPNG' | 'toJPEG'](chartElement, {
  //         quality: 0.95,
  //         backgroundColor: 'white',
  //       });
  //       saveAs(dataUrl, `${t('locked_users_kpi')}-${selectedPeriod.label}.${format}`);
  //     }
      
  //     chartElement.style.backgroundColor = originalBackground;
  //     chartElement.style.padding = '';
      
  //   } catch (err) {
  //     console.error('Download error:', err);
  //     alert(t('download_error'));
  //   }
  // };

  // if (isLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-64" aria-label={t('loading')}>
  //       {t('loading')}
  //     </div>
  //   );
  // }
  // if (error) {
  //   return (
  //     <div className="flex justify-center items-center h-64 text-red-500" aria-label={t('error_loading')}>
  //       {t('error_loading')}
  //     </div>
  //   );
  // }
  // Download function corrigée
const handleDownload = async (format: 'png' | 'jpeg' | 'pdf') => {
  if (!chartRef.current) return;

  try {
    const chartElement = chartRef.current;

    // Sauvegarder le style original
    const originalBackground = chartElement.style.backgroundColor;
    const originalPadding = chartElement.style.padding;

    chartElement.style.backgroundColor = 'white';
    chartElement.style.padding = '20px';

    let dataUrl: string;

    if (format === 'pdf') {
      dataUrl = await htmlToImage.toPng(chartElement);
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('landscape');
      pdf.addImage(dataUrl, 'PNG', 15, 15, 260, 150);
      pdf.save(`${t('locked_users_kpi')}-${selectedPeriod.label}.pdf`);
    } else {
      const formatFunctionMap: Record<'png' | 'jpeg', (node: HTMLElement, options?: any) => Promise<string>> = {
        png: htmlToImage.toPng,
        jpeg: htmlToImage.toJpeg,
      };

      const exportFn = formatFunctionMap[format];
      dataUrl = await exportFn(chartElement, {
        quality: 0.95,
        backgroundColor: 'white',
      });

      saveAs(dataUrl, `${t('locked_users_kpi')}-${selectedPeriod.label}.${format}`);
    }

    // Restaurer les styles d’origine
    chartElement.style.backgroundColor = originalBackground;
    chartElement.style.padding = originalPadding;

  } catch (err) {
    console.error('Download error:', err);
    alert(t('download_error'));
  }
};


  return (
    <div className="bg-white shadow rounded-lg p-6" ref={chartRef}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <Lock className="mr-2 text-red-500" size={20} />
          {t('account_lock_status')}
        </h2>
        
        <div className="flex space-x-2">
          <div className="relative">
            <button
              onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
              aria-label={t('select_period')}
            >
              <Calendar size={16} />
              <span>{selectedPeriod.label}</span>
              {isPeriodDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            
            {isPeriodDropdownOpen && (
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedPeriod(option);
                      setIsPeriodDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      selectedPeriod.value === option.value ? 'bg-gray-100 font-medium' : ''
                    }`}
                    aria-label={t('select_option', { label: option.label })}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="relative">
            <button
              onClick={() => document.getElementById('download-dropdown-locked')?.classList.toggle('hidden')}
              className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
              aria-label={t('download')}
            >
              <Download size={16} />
              <span>{t('download')}</span>
            </button>
            
            <div id="download-dropdown-locked" className="hidden absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
              <button
                onClick={() => handleDownload('png')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_png')}
              >
                {t('png')}
              </button>
              <button
                onClick={() => handleDownload('jpeg')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_jpeg')}
              >
                {t('jpeg')}
              </button>
              <button
                onClick={() => handleDownload('pdf')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_pdf')}
              >
                {t('pdf')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              type="number" 
              domain={[0, 100]} 
              tick={{ fill: '#4a4a4a' }}
              axisLine={{ stroke: '#4a4a4a' }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={120}
              tick={{ fill: '#4a4a4a' }}
              axisLine={{ stroke: '#4a4a4a' }}
            />
            <Tooltip 
              formatter={(value) => [`${value}%`, t('percentage')]}
              contentStyle={{ backgroundColor: '#ffffff', borderColor: '#c06a70' }}
            />
            <Bar dataKey="value" barSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
              <LabelList 
                dataKey="value" 
                position="right" 
                formatter={(value: any) => `${value}%`}
                fill="#3d3d3d"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>
          {t('locked_user_summary', {
            percentage: lockedUserStats.percentage,
            count: lockedUserStats.count,
            totalUsers: lockedUserStats.totalUsers,
          })}
        </p>
        {selectedPeriod.value !== 3650 && (
          <p className="mt-1">
            {t('recent_locks_summary', {
              count: lockedUserStats.recentLocks,
              period: selectedPeriod.label,
            })}
          </p>
        )}
        {lockedUserStats.percentage > 10 && (
          <p className="mt-2 text-yellow-600 flex items-center">
            <Lock className="mr-1" size={14} />
            {t('high_lock_warning')}
          </p>
        )}
      </div>
    </div>
  );
}

// // LockedUsersChart.tsx
// import { useState, useEffect, useRef } from 'react';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
// import { Calendar, ChevronUp, ChevronDown, Download, Lock } from 'lucide-react';
// import useSWR from 'swr';
// import axios from 'axios';
// import * as htmlToImage from 'html-to-image';
// import { saveAs } from 'file-saver';
// import { useParams } from 'react-router-dom';

// // Types
// interface User {
//   id: string;
//   status: string; // 'Active', 'Locked', 'New'
//   lockedDate?: string; // ISO date string when user was locked
// }

// interface PeriodOption {
//   label: string;
//   value: number; // in days
// }

// // Period options
// const PERIOD_OPTIONS: PeriodOption[] = [
//   { label: '24 hours', value: 1 },
//   { label: '7 days', value: 7 },
//   { label: '30 days', value: 30 },
//   { label: 'All time', value: 3650 }, // ~10 years for "all time"
// ];

// // Fetcher for SWR
// const axiosFetcher = async (url: string) => {
//   const response = await axios.get(url, {
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//     },
//   });
//   return response.data;
// };

// export default function LockedUsersChart() {
//   const companyId = localStorage.getItem('company');
//   const { appId } = useParams<{ appId: string }>();
//   const API_BASE_URL = import.meta.env.VITE_API;
  
//   const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(PERIOD_OPTIONS[1]); // Default to 7 days
//   const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
//   const chartRef = useRef<HTMLDivElement>(null);
  
//   // Fetch user data
//   const { data: usersData, error, isLoading } = useSWR<User[]>(
//     companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}/users` : null,
//     axiosFetcher
//   );

//   // Calculate locked user statistics
//   const [lockedUserStats, setLockedUserStats] = useState({
//     percentage: 0,
//     count: 0,
//     totalUsers: 0,
//     recentLocks: 0, // Locks in selected period
//   });
  
//   useEffect(() => {
//     if (usersData?.data) {
//       const now = new Date();
//       const periodStart = new Date();
//       periodStart.setDate(now.getDate() - selectedPeriod.value);
      
//       const users = usersData.data;
//       const totalUsers = users.length;
      
//       if (totalUsers === 0) {
//         setLockedUserStats({
//           percentage: 0,
//           count: 0,
//           totalUsers: 0,
//           recentLocks: 0,
//         });
//         return;
//       }
      
//       const lockedUsers = users.filter(user => user.status === 'Locked');
//       const recentLocks = lockedUsers.filter(user => {
//         if (!user.lockedDate) return false;
//         const lockDate = new Date(user.lockedDate);
//         return lockDate >= periodStart;
//       }).length;
      
//       const percentage = Math.round((lockedUsers.length / totalUsers) * 100);
//       setLockedUserStats({
//         percentage,
//         count: lockedUsers.length,
//         totalUsers,
//         recentLocks,
//       });
//     }
//   }, [usersData, selectedPeriod]);

//   // Chart data
//   const chartData = [
//     {
//       name: `Locked users`,
//       value: lockedUserStats.percentage,
//       fill: '#c06a70', // Red-pink color from your palette
//     },
//     {
//       name: 'Active users',
//       value: 100 - lockedUserStats.percentage,
//       fill: '#879bae', // Gray-blue color
//     },
//   ];

//   // Download function
//   const handleDownload = async (format: 'png' | 'jpeg' | 'pdf') => {
//     if (!chartRef.current) return;

//     try {
//       const chartElement = chartRef.current;
      
//       // Temporary style for better image rendering
//       const originalBackground = chartElement.style.backgroundColor;
//       chartElement.style.backgroundColor = 'white';
//       chartElement.style.padding = '20px';
      
//       let dataUrl;
//       if (format === 'pdf') {
//         dataUrl = await htmlToImage.toPng(chartElement);
//         const { jsPDF } = await import('jspdf');
//         const pdf = new jsPDF('landscape');
//         pdf.addImage(dataUrl, 'PNG', 15, 15, 260, 150);
//         pdf.save(`Locked-Users-KPI-${selectedPeriod.label}.pdf`);
//       } else {
//         dataUrl = await htmlToImage[`to${format.toUpperCase()}` as 'toPNG' | 'toJPEG'](chartElement, {
//           quality: 0.95,
//           backgroundColor: 'white',
//         });
//         saveAs(dataUrl, `Locked-Users-KPI-${selectedPeriod.label}.${format}`);
//       }
      
//       // Restore original style
//       chartElement.style.backgroundColor = originalBackground;
//       chartElement.style.padding = '';
      
//     } catch (err) {
//       console.error('Download error:', err);
//       alert('An error occurred while downloading');
//     }
//   };

//   if (isLoading) return <div className="flex justify-center items-center h-64">Loading...</div>;
//   if (error) return <div className="flex justify-center items-center h-64 text-red-500">Error loading data</div>;

//   return (
//     <div className="bg-white shadow rounded-lg p-6" ref={chartRef}>
//       <div className="flex justify-between items-center mb-4">
//         <h2 className="text-lg font-medium text-gray-900 flex items-center">
//           <Lock className="mr-2 text-red-500" size={20} />
//           Account Lock Status
//         </h2>
        
//         <div className="flex space-x-2">
//           {/* Period selector */}
//           <div className="relative">
//             <button
//               onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
//               className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
//             >
//               <Calendar size={16} />
//               <span>{selectedPeriod.label}</span>
//               {isPeriodDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
//             </button>
            
//             {isPeriodDropdownOpen && (
//               <div className="absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
//                 {PERIOD_OPTIONS.map((option) => (
//                   <button
//                     key={option.value}
//                     onClick={() => {
//                       setSelectedPeriod(option);
//                       setIsPeriodDropdownOpen(false);
//                     }}
//                     className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
//                       selectedPeriod.value === option.value ? 'bg-gray-100 font-medium' : ''
//                     }`}
//                   >
//                     {option.label}
//                   </button>
//                 ))}
//               </div>
//             )}
//           </div>
          
//           {/* Download button */}
//           <div className="relative">
//             <button
//               onClick={() => document.getElementById('download-dropdown-locked')?.classList.toggle('hidden')}
//               className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
//             >
//               <Download size={16} />
//               <span>Download</span>
//             </button>
            
//             <div id="download-dropdown-locked" className="hidden absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
//               <button
//                 onClick={() => handleDownload('png')}
//                 className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
//               >
//                 PNG
//               </button>
//               <button
//                 onClick={() => handleDownload('jpeg')}
//                 className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
//               >
//                 JPEG
//               </button>
//               <button
//                 onClick={() => handleDownload('pdf')}
//                 className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
//               >
//                 PDF
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
      
//       <div className="h-64">
//         <ResponsiveContainer width="100%" height="100%">
//           <BarChart
//             data={chartData}
//             layout="vertical"
//             margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
//           >
//             <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
//             <XAxis 
//               type="number" 
//               domain={[0, 100]} 
//               tick={{ fill: '#4a4a4a' }}
//               axisLine={{ stroke: '#4a4a4a' }}
//             />
//             <YAxis 
//               dataKey="name" 
//               type="category" 
//               width={120}
//               tick={{ fill: '#4a4a4a' }}
//               axisLine={{ stroke: '#4a4a4a' }}
//             />
//             <Tooltip 
//               formatter={(value) => [`${value}%`, 'Percentage']}
//               contentStyle={{ backgroundColor: '#ffffff', borderColor: '#c06a70' }}
//             />
//             <Bar dataKey="value" barSize={30}>
//               {chartData.map((entry, index) => (
//                 <Cell key={`cell-${index}`} fill={entry.fill} />
//               ))}
//               <LabelList 
//                 dataKey="value" 
//                 position="right" 
//                 formatter={(value) => `${value}%`}
//                 fill="#3d3d3d"
//               />
//             </Bar>
//           </BarChart>
//         </ResponsiveContainer>
//       </div>
      
//       <div className="mt-4 text-sm text-gray-500">
//         <p>
//           <span className="font-medium">{lockedUserStats.percentage}%</span> of accounts ({lockedUserStats.count}/{lockedUserStats.totalUsers}) 
//           are currently locked.
//         </p>
//         {selectedPeriod.value !== 3650 && (
//           <p className="mt-1">
//             <span className="font-medium">{lockedUserStats.recentLocks}</span> accounts were locked in the last {selectedPeriod.label}.
//           </p>
//         )}
//         {lockedUserStats.percentage > 10 && (
//           <p className="mt-2 text-yellow-600 flex items-center">
//             <Lock className="mr-1" size={14} />
//             Warning: High lock rate may indicate security issues
//           </p>
//         )}
//       </div>
//     </div>
//   );
// }